import FocusTrap from '@pinecast/common/FocusTrap';
import styled, {focusVisibleRing} from '@pinecast/styles';
import * as React from 'react';
import type {JSX} from 'react';

import {DEFAULT_FONT} from './constants';
import type {LocalizedReactNode} from './types';

const WashContainer = styled('div', {
  alignItems: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  justifyContent: 'center',
  left: 0,
  position: 'fixed',
  top: 0,
  width: '100vw',
  zIndex: 100,
});
const WashInnerContainer = styled('div', {
  flex: '0 0 100%',
  maxHeight: '100%',
  overflowY: 'auto',
  padding: '15vh 20px',
  width: '100%',
  '@media (max-width: 700px)': {
    padding: '15vh 12px',
  },
});
// The backdrop behind a modal dialog, and the element that holds all of it.
// `ref` is where `useDialogIsolation` goes.
export const Wash = ({
  children,
  onClick,
  ref: containerRef,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  ref?: React.Ref<HTMLDivElement>;
}) => {
  const ref = React.useRef<null | HTMLElement>(null);
  return (
    <WashContainer onClick={onClick} ref={containerRef}>
      <WashInnerContainer
        ref={ref}
        onClick={React.useCallback(
          (e: React.SyntheticEvent<HTMLDivElement>) => {
            e.stopPropagation();
            if (e.target === ref.current) {
              onClick?.();
            }
          },
          [onClick],
        )}
      >
        <FocusTrap>{children}</FocusTrap>
      </WashInnerContainer>
    </WashContainer>
  );
};

const Wrapper = styled(
  'div',
  ({$size}: {$size: 'small' | 'medium' | 'large'}, designColors) => ({
    backgroundColor: designColors.contentBackground,
    borderColor: designColors.contentBorder,
    borderStyle: 'solid',
    borderTopWidth: '1px',
    borderBottomWidth: '2px',
    borderLeftWidth: '1px',
    borderRightWidth: '1px',
    borderRadius: 6,
    boxShadow:
      'rgba(0, 0, 0, 0.2) 0px 3px 5px, rgba(0, 0, 0, 0.2) 0px 6px 10px',
    color: designColors.contentText,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: DEFAULT_FONT,
    margin: '0 auto',
    maxWidth: $size === 'small' ? 400 : $size === 'medium' ? 625 : 850,
    // minWidth: $size === 'small' ? 350 : $size === 'medium' ? 550 : 650,
    overflow: 'hidden',
    textAlign: 'left',
    width: '100%',
  }),
);
const Header = styled('header', (_, designColors) => ({
  alignItems: 'center',
  borderBottom: `1px solid ${designColors.contentBorder}`,
  display: 'flex',
  fontSize: 15,
  fontFamily: 'Monument Grotesk Bold',
  height: 46,
  padding: '0 15px',
}));
// A dialog with no field takes focus on its heading when it opens.
const Title = styled('h2', (_, designColors) => ({
  fontFamily: 'inherit',
  fontSize: 'inherit',
  fontWeight: 'inherit',
  lineHeight: 'inherit',
  margin: 0,
  ':focus:not(:focus-visible)': {outline: 'none'},
  ...focusVisibleRing(designColors),
}));
const Body = styled(
  'section',
  ({$size}: {$size: 'small' | 'medium' | 'large'}, designColors) => ({
    // backgroundColor: designColors.background,
    fontSize: 13,
    minHeight: $size === 'small' ? 80 : $size === 'medium' ? 120 : 240,
    padding: 15,
  }),
);
const Footer = styled('footer', (_, designColors) => ({
  alignItems: 'center',
  backgroundColor: designColors.contentBackground,
  display: 'flex',
  height: 46,
  justifyContent: 'flex-end',
  padding: '0 15px',
  textAlign: 'right',
}));

type Props = {
  actions?: React.ReactNode;
  children: React.ReactNode;
  size?: 'small' | 'medium' | 'large';
  title: LocalizedReactNode;
};

const Dialog = ({actions, children, size, title}: Props) => {
  const id = React.useId();

  let actionWrapper: JSX.Element | null = null;
  if (actions) {
    actionWrapper = <Footer>{actions}</Footer>;
  }

  return (
    <Wrapper
      aria-modal
      role="dialog"
      $size={size || 'medium'}
      aria-labelledby={`${id}__title`}
      aria-describedby={`${id}__body`}
    >
      <Header>
        <Title id={`${id}__title`} tabIndex={-1}>
          {title}
        </Title>
      </Header>
      <Body id={`${id}__body`} $size={size || 'medium'}>
        {children}
      </Body>
      {actionWrapper}
    </Wrapper>
  );
};

export default React.memo(Dialog);
